import React, { useCallback, useEffect, useState } from "react";
//img
import bellIcon from "../../../assets/images/svg/Notification.svg";
//css
import classes from "./NotificationBell.module.css";
import { CircularProgress, Drawer, Tooltip } from "@mui/material";
import axios from "axios";
import { API_ENDPOINT } from "../../../services/api/resources";
import { useDispatch, useSelector } from "react-redux";
import {
  markAllNotificationsRead,
  markNotificationRead,
} from "../../../features/NotificationSlice";
import { Close } from "@mui/icons-material";
import NotificationCard from "./NotificationCard";
import {
  applyLocalReadStateToMessages,
  CHAT_READ_STATE_EVENT,
} from "../../../utils/chatReadState";
import { OPEN_NOTIFICATION_PANEL_EVENT } from "../../../utils/notificationEvents";

const normalizeTextValue = (value) => String(value ?? "").trim();

const normalizeNotificationDate = (item) =>
  item?.created ||
  item?.created_at ||
  item?.created_on ||
  item?.created_date ||
  item?.date ||
  "";

const normalizeNotificationStatus = (status) => {
  if (status === true || status === false) return status;
  const normalizedStatus = normalizeTextValue(status).toLowerCase();

  if (
    status === 1 ||
    status === "1" ||
    normalizedStatus === "true" ||
    normalizedStatus === "read" ||
    normalizedStatus === "seen" ||
    normalizedStatus === "viewed"
  ) {
    return true;
  }
  if (
    status === 0 ||
    status === "0" ||
    normalizedStatus === "false" ||
    normalizedStatus === "unread" ||
    normalizedStatus === "new"
  ) {
    return false;
  }
  return false;
};

const getNotificationPrimaryId = (item = {}) =>
  item?.id ||
  item?.notify_id ||
  item?.notification_id ||
  item?.message_id ||
  null;

const getNotificationMergeKey = (item = {}, fallbackKey = "") => {
  const notificationToken = normalizeTextValue(
    item?.token || item?.notification_token || item?.message_token,
  );
  if (notificationToken) return `token:${notificationToken}`;

  const notificationId = getNotificationPrimaryId(item);
  if (notificationId) return `id:${notificationId}`;

  const message = normalizeTextValue(
    item?.message ||
      item?.body ||
      item?.description ||
      item?.comments ||
      item?.text,
  );
  const createdRaw = normalizeTextValue(normalizeNotificationDate(item));
  const buyerKey = normalizeTextValue(item?.buyer || item?.buyer_token);
  const vendorKey = normalizeTextValue(item?.vendor || item?.vendor_token);
  const rfqKey = normalizeTextValue(
    item?.rfq || item?.rfq_id || item?.rfq_token,
  );
  const bidKey = normalizeTextValue(
    item?.bid || item?.bid_id || item?.bid_token,
  );

  return `fallback:${message}|${createdRaw}|${buyerKey}|${vendorKey}|${rfqKey}|${bidKey}|${fallbackKey}`;
};

const getNotificationResolvedStatus = (item = {}) => {
  const statusKeys = [
    "status",
    "read_status",
    "is_read",
    "isRead",
    "read",
    "seen",
    "viewed",
    "notification_status",
    "notification_read_status",
  ];

  for (const key of statusKeys) {
    const value = item?.[key];

    if (
      value === undefined ||
      value === null ||
      normalizeTextValue(value) === ""
    ) {
      continue;
    }

    return normalizeNotificationStatus(value);
  }

  const unreadKeys = ["unread", "is_unread", "isUnread"];

  for (const key of unreadKeys) {
    const value = item?.[key];

    if (
      value === true ||
      value === 1 ||
      normalizeTextValue(value).toLowerCase() === "true"
    ) {
      return false;
    }

    if (
      value === false ||
      value === 0 ||
      normalizeTextValue(value).toLowerCase() === "false"
    ) {
      return true;
    }
  }

  // Important:
  // If backend did not send any status field, don't treat it as unread.
  return true;
};
const formatNotificationDate = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const markNotificationReadInList = (items = [], notificationTarget) =>
  (Array.isArray(items) ? items : []).map((item) => {
    const targetId =
      typeof notificationTarget === "object"
        ? getNotificationPrimaryId(notificationTarget)
        : notificationTarget;
    const targetMergeKey =
      typeof notificationTarget === "object"
        ? getNotificationMergeKey(notificationTarget)
        : notificationTarget
          ? `id:${notificationTarget}`
          : "";
    const itemId = getNotificationPrimaryId(item);
    const itemMergeKey = getNotificationMergeKey(item);
    const isMatchingId =
      targetId !== undefined &&
      targetId !== null &&
      String(itemId) === String(targetId);
    const isMatchingMergeKey =
      Boolean(targetMergeKey) && itemMergeKey === targetMergeKey;

    return isMatchingId || isMatchingMergeKey
      ? { ...item, status: true }
      : item;
  });

const markAllNotificationsReadInList = (items = []) =>
  (Array.isArray(items) ? items : []).map((item) => ({
    ...item,
    status: true,
  }));

const getFilteredNotifications = (items = [], filter = "All") => {
  const normalizedItems = Array.isArray(items) ? items : [];

  if (filter === "Read") {
    return normalizedItems.filter(
      (item) => getNotificationResolvedStatus(item) === true,
    );
  }

  if (filter === "Unread") {
    return normalizedItems.filter(
      (item) => getNotificationResolvedStatus(item) === false,
    );
  }

  return normalizedItems;
};

const mergeNotificationEntries = (existingItem, nextItem) => ({
  ...existingItem,
  ...nextItem,
  _rowKey: existingItem?._rowKey || nextItem?._rowKey,
  id: existingItem?.id || nextItem?.id,
  title: existingItem?.title || nextItem?.title,
  message: existingItem?.message || nextItem?.message,
  created: existingItem?.created || nextItem?.created,
  created_raw: existingItem?.created_raw || nextItem?.created_raw,
  status: existingItem?.status === true || nextItem?.status === true,
});

const NotificationBell = () => {
  //getting data from use selector
  const loginData = useSelector((state) => state.loginData.value);
  const dispatch = useDispatch();
  const notificationsData = useSelector((state) => state.notifications.items);
  const [loadingState, setLoadingState] = useState(false);
  const [panelLoading, setPanelLoading] = useState(false);

  // opensidebar
  const [openNotificationBar, setOpenNotificationBar] = useState(false);
  const handleCloseNotificationBar = () => setOpenNotificationBar(false);

  const [approvalNotificationData, setApprovalNotificationData] = useState([]);
  const [viewOption, setViewOption] = useState("All");
  const [getusermessages, setUserMessages] = useState([]);

  const accessType = String(loginData?.access_type || "").toLowerCase();
  const auth_token = loginData?.auth_token || loginData?.token || "";
  const buyer_token =
    loginData?.user?.token ||
    loginData?.buyer_token ||
    loginData?.organization_token ||
    "";
  const user_token = loginData?.user?.user_token || loginData?.user_token || "";
  const vendor_token =
    loginData?.user?.token ||
    loginData?.vendor_contact_token ||
    loginData?.vendor_token ||
    "";

  const handleOptionChange = (e) => {
    const val = e?.target?.value;
    setViewOption(val);
  };

  const org_buyer_access = accessType === "buyer" || accessType === "admin";
  const user_access = accessType === "user";
  const vendor_admin_access = accessType === "vendor admin";
  const vendor_contact_access = accessType === "vendor contact";

  const getuserMessages = useCallback(async () => {
    if (!auth_token || !user_token) return;
    const formData = new FormData();
    formData.append("user_token", user_token);

    await axios({
      method: "POST",
      data: formData,
      url: `${API_ENDPOINT.get_user_messages}`,
      headers: {
        Authorization: `Bearer ${auth_token}`,
      },
    })
      .then((res) => {
        setUserMessages(applyLocalReadStateToMessages(res?.data, user_token));
      })
      .catch((err) => {
        console.log(err);
        setUserMessages([]);
      });
  }, [auth_token, user_token]);

  const approvalNotifications = useCallback(async () => {
    if (!auth_token) return;
    const formData = new FormData();
    if (org_buyer_access && buyer_token) {
      formData.append("buyer_token", buyer_token);
    }
    if (user_access && user_token) {
      formData.append("user_token", user_token);
    }

    await axios({
      method: "POST",
      url: `${API_ENDPOINT.approvalNotifications}`,
      data: formData,
      headers: {
        Authorization: `Bearer ${auth_token}`,
      },
    })
      .then((res) => {
        const notifications =
          res?.data?.notifications ||
          (res?.data?.status != "error" && res?.data);
        if (notifications) {
          if (Array.isArray(notifications)) {
            setApprovalNotificationData(notifications);
          } else {
            setApprovalNotificationData([notifications]);
          }
        } else {
          setApprovalNotificationData([]);
        }
      })
      .catch((err) => {
        console.log(err);
        setApprovalNotificationData([]);
      });
  }, [auth_token, buyer_token, org_buyer_access, user_access, user_token]);

  const refreshNotifications = useCallback(async () => {
    if (!auth_token) return;
    setPanelLoading(true);
    await Promise.allSettled([
      approvalNotifications(),
      ...(user_access ? [getuserMessages()] : []),
    ]);
    setPanelLoading(false);
  }, [
    approvalNotifications,
    auth_token,
    getuserMessages,
    user_access,
  ]);

  const normalizeNotifications = (items = []) =>
    Array.from(
      (Array.isArray(items) ? items : [])
        .reduce((map, item, index) => {
          const message =
            item?.message ||
            item?.body ||
            item?.description ||
            item?.comments ||
            item?.text ||
            "";
          const title =
            item?.title ||
            item?.subject ||
            item?.heading ||
            item?.notification_title ||
            item?.name ||
            "";
          const createdRaw = normalizeNotificationDate(item);
          const normalizedItem = {
            ...item,
            _rowKey:
              getNotificationPrimaryId(item) ||
              item?.token ||
              item?.notification_token ||
              `${createdRaw}-${index}`,
            id: getNotificationPrimaryId(item),
            message: normalizeTextValue(message),
            title: normalizeTextValue(title),
            created: formatNotificationDate(createdRaw),
            created_raw: createdRaw,
            status: getNotificationResolvedStatus(item),
          };

          if (!normalizedItem.message) {
            return map;
          }

          const dedupeKey = getNotificationMergeKey(
            item,
            `${createdRaw}-${index}`,
          );
          const existingItem = map.get(dedupeKey);
          map.set(
            dedupeKey,
            existingItem
              ? mergeNotificationEntries(existingItem, normalizedItem)
              : normalizedItem,
          );
          return map;
        }, new Map())
        .values(),
    ).sort(
      (a, b) =>
        new Date(b.created_raw || 0).getTime() -
        new Date(a.created_raw || 0).getTime(),
    );

  const handleOpenNotificationBar = useCallback(async () => {
    setOpenNotificationBar(true);
    await refreshNotifications();
  }, [refreshNotifications]);

  useEffect(() => {
    const handleOpenNotificationPanel = () => {
      handleOpenNotificationBar();
    };

    window.addEventListener(
      OPEN_NOTIFICATION_PANEL_EVENT,
      handleOpenNotificationPanel,
    );

    return () => {
      window.removeEventListener(
        OPEN_NOTIFICATION_PANEL_EVENT,
        handleOpenNotificationPanel,
      );
    };
  }, [handleOpenNotificationBar]);

  const mergeAndSortNotifications = () =>
    normalizeNotifications([...approvalNotificationData, ...notificationsData]);

  const notificationClick = async (notificationTarget, markAsAllReadKeys) => {
    const formData = new FormData();
    const isMarkAsAllRead =
      Array.isArray(markAsAllReadKeys) && markAsAllReadKeys.length > 0;
    const notificationId =
      typeof notificationTarget === "object"
        ? getNotificationPrimaryId(notificationTarget)
        : notificationTarget;

    notificationId && formData.append("notify_id", notificationId);

    if (notificationId) {
      dispatch(markNotificationRead(notificationId));
      setApprovalNotificationData((prev) =>
        markNotificationReadInList(prev, notificationTarget),
      );
      setUserMessages((prev) =>
        markNotificationReadInList(prev, notificationTarget),
      );
    }

    if (isMarkAsAllRead) {
      dispatch(markAllNotificationsRead());
      setApprovalNotificationData((prev) =>
        markAllNotificationsReadInList(prev),
      );
      setUserMessages((prev) => markAllNotificationsReadInList(prev));

      markAsAllReadKeys.forEach((element) => {
        return formData.append(element.key, element.value);
      });
    }

    await axios({
      method: "POST",
      url: `${API_ENDPOINT.unreadNotifications}`,
      data: formData,
      headers: {
        Authorization: `Bearer ${auth_token}`,
      },
    })
      .then(() => {
        setLoadingState(false);

        if (!isMarkAsAllRead) {
          approvalNotifications();
          if (user_access) {
            getuserMessages();
          }
        }
      })
      .catch((err) => {
        setLoadingState(false);
        if (notificationId || isMarkAsAllRead) {
          approvalNotifications();
          if (user_access) {
            getuserMessages();
          }
        }
        console.log(err);
      });
  };

  const markAsAllRead = () => {
    setLoadingState(true);
    let didRequestMarkAll = false;

    if (org_buyer_access && buyer_token) {
      didRequestMarkAll = true;
      notificationClick(null, [
        {
          key: "buyer_token",
          value: buyer_token,
        },
      ]);
    }
    if (user_access && user_token) {
      didRequestMarkAll = true;
      notificationClick(null, [
        {
          key: "user_token",
          value: user_token,
        },
      ]);
    }
    if (vendor_admin_access && vendor_token) {
      didRequestMarkAll = true;
      notificationClick(null, [
        {
          key: "vendor_token",
          value: vendor_token,
        },
      ]);
    }
    if (vendor_contact_access && vendor_token) {
      didRequestMarkAll = true;
      notificationClick(null, [
        {
          key: "vendor_token",
          value: vendor_token,
        },
      ]);
    }

    if (!didRequestMarkAll) {
      setLoadingState(false);
    }
  };

  useEffect(() => {
    const handleChatReadStateChange = (event) => {
      if (
        !event?.detail?.userToken ||
        String(event.detail.userToken) === String(user_token)
      ) {
        setUserMessages((prev) =>
          applyLocalReadStateToMessages(prev, user_token),
        );
      }
    };

    window.addEventListener(CHAT_READ_STATE_EVENT, handleChatReadStateChange);
    return () => {
      window.removeEventListener(
        CHAT_READ_STATE_EVENT,
        handleChatReadStateChange,
      );
    };
  }, [user_token]);

  const combinedNotifications = mergeAndSortNotifications();
  const displayedNotifications = combinedNotifications;
  const filteredNotifications = getFilteredNotifications(
    displayedNotifications,
    viewOption,
  );
  const unreadFilteredCount = filteredNotifications.filter(
    (notification) => notification?.status === false,
  ).length;
  const disableMarkAllRead = loadingState || unreadFilteredCount === 0;
  const hasUnreadNotifications = displayedNotifications.some(
    (notification) => notification.status === false,
  );

  return (
    <div className={`${classes.notification_wrapper} ${classes.flex}`}>
      <Tooltip
        title="Alerts"
        arrow
        componentsProps={{
          tooltip: {
            sx: {
              fontSize: "1.1rem",
              padding: "0.8rem 1.1rem",
            },
          },
          
        }}
      >
        <div
          className={`${classes.notification_icon} ${
            hasUnreadNotifications ? `${classes.active}` : ""
          }`}
          onClick={handleOpenNotificationBar}
        >
          <img src={bellIcon} />
          {hasUnreadNotifications && (
            <span className={`${classes.notification_indicator}`}></span>
          )}
        </div>
      </Tooltip>

      <Drawer
        open={openNotificationBar}
        anchor={"right"}
        className={classes.menuDraw}
        onClose={handleCloseNotificationBar}
      >
        <div className={`${classes.notification_panel}`}>
          <div className={`${classes.notificationHeader}`}>
            <span>Notifications</span>
            <Close
              sx={{ fontSize: "3rem", padding: "0.5rem", cursor: "pointer" }}
              className={classes.closeBtn}
              onClick={handleCloseNotificationBar}
            />
          </div>
          <div className={classes.filterOptions}>
            <div className={classes.filterDropdownGroup}>
              {/* <span className={classes.filterDropdownLabel}>View</span> */}
              <div className={classes.filterDropdownWrap}>
                <select
                  className={classes.filterDropdown}
                  onChange={handleOptionChange}
                  value={viewOption}
                >
                  <option value="All">All </option>
                  <option value="Read">Read </option>
                  <option value="Unread">Unread </option>
                </select>
              </div>
            </div>

            <button
              className={`${classes.markAsAllRead} ${
                disableMarkAllRead ? classes.markAsAllReadDisabled : ""
              }`}
              onClick={() => {
                if (!disableMarkAllRead) {
                  markAsAllRead();
                }
              }}
              disabled={disableMarkAllRead}
            >
              {!loadingState ? (
                "Mark all as read"
              ) : (
                <CircularProgress
                  size={"15px"}
                  thickness={6}
                  variant="indeterminate"
                  disableShrink
                  sx={{
                    color: "#fff",
                    animationDuration: "450ms",
                  }}
                />
              )}
            </button>
          </div>

          <NotificationCard
            notificationsData={filteredNotifications}
            isLoading={panelLoading}
            notificationClick={notificationClick}
          />
        </div>
      </Drawer>
    </div>
  );
};

export default NotificationBell;
